/**
 * useInstallPrompt — captures the browser's `beforeinstallprompt` event so
 * Advoset can render its own branded install UI instead of relying on the
 * default browser chrome. Also listens for `appinstalled` to lock the prompt
 * out permanently once install succeeds.
 *
 * Returns:
 *   {
 *     canInstall  — true when we have a deferred prompt AND we're allowed to show it,
 *     promptInstall — async fn that triggers the native install dialog,
 *     dismiss(daysSnooze = 30) — user tapped Maybe Later,
 *     isInstalled — set after `appinstalled` fires (or already-standalone),
 *   }
 *
 * Never runs its side-effects when the page is already a standalone PWA
 * (nothing to install). Silently no-ops on browsers that don't support the
 * event (Firefox, iOS Safari) — the caller just sees `canInstall=false`.
 */
import { useCallback, useEffect, useState } from "react";
import { trackInstallEvent } from "@/lib/pwaAnalytics";

const DISMISS_KEY = "advoset:pwa:installDismissedAt";
const INSTALLED_KEY = "advoset:pwa:installed";
const DEFAULT_SNOOZE_DAYS = 30;

function isStandalone() {
  if (typeof window === "undefined") return false;
  try {
    if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return true;
  } catch (_) { /* older browsers */ }
  return typeof window.navigator?.standalone === "boolean" && window.navigator.standalone;
}

function readInstalled() {
  if (isStandalone()) return true;
  try { return localStorage.getItem(INSTALLED_KEY) === "1"; }
  catch (_) { return false; }
}

function isSnoozed() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (!Number.isFinite(ts)) return false;
    const ageMs = Date.now() - ts;
    // 30-day snooze by default — configurable at call-time via dismiss(days).
    return ageMs < DEFAULT_SNOOZE_DAYS * 24 * 60 * 60 * 1000;
  } catch (_) { return false; }
}

export function useInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [isInstalled, setIsInstalled] = useState(() => readInstalled());
  const [snoozed, setSnoozed] = useState(() => isSnoozed());

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    // Nothing to install if the page is already installed.
    if (isStandalone()) { setIsInstalled(true); return undefined; }

    const onBip = (e) => {
      // Suppress the browser's native mini-infobar so our card can lead the flow.
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => {
      setIsInstalled(true);
      setDeferred(null);
      try { localStorage.setItem(INSTALLED_KEY, "1"); } catch (_) { /* ignore */ }
      trackInstallEvent("app_installed", "web_prompt");
    };
    window.addEventListener("beforeinstallprompt", onBip);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBip);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferred) return { outcome: "unsupported" };
    try {
      deferred.prompt();
      const choice = await deferred.userChoice;
      // Once used, the event is spent — Chrome fires a new one only on a new page load.
      setDeferred(null);
      if (choice?.outcome === "accepted") {
        setIsInstalled(true);
        try { localStorage.setItem(INSTALLED_KEY, "1"); } catch (_) { /* ignore */ }
        trackInstallEvent("install_accepted", "web_prompt");
      } else {
        // User dismissed the NATIVE dialog — treat as a soft snooze.
        try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (_) { /* ignore */ }
        setSnoozed(true);
        trackInstallEvent("install_dismissed", "web_prompt");
      }
      return choice;
    } catch (_) {
      setDeferred(null);
      return { outcome: "error" };
    }
  }, [deferred]);

  const dismiss = useCallback((_daysSnooze) => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (_) { /* ignore */ }
    setSnoozed(true);
    trackInstallEvent("install_dismissed", "web_prompt");
  }, []);

  const canInstall = !!deferred && !isInstalled && !snoozed;
  return { canInstall, promptInstall, dismiss, isInstalled };
}
